import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/commercial-effectiveness/
// commercial-effectiveness.data-lake-source.ts SOURCE_CONFIGS (one contextKind
// per evidence key) and commercial-effectiveness.types.ts
// (COMMERCIAL_EFFECTIVENESS_DATA_LAKE_SCHEMA_VERSION).
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): one APPROVED,
// ingest-anchored row under each of the four context kinds, all at
// commercial_effectiveness_evidence_prep_bundle_v1. A clean four-for-four match
// between what the manifest declares, what the engine queries, and what exists.
//
// FRESHNESS NOTE (disclosed, #4449 lane 1): every row carries asOf
// 2025-09-30 — roughly 312 days old at registration. Against a `weekly` (10-day)
// ceiling for the pipeline snapshot and `monthly` (45-day) for the other three,
// all four read STALE. That is the honest result and the reason to register
// anyway: it converts an unmeasurable CONTRACT_ABSENT ("we cannot see this
// module's evidence") into a precise, actionable staleness finding ("these four
// artifacts exist, are approved, and are 312 days old"). The fix is a data
// refresh, not a contract change.
export const COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS = {
  pipelineSnapshot: 'approved_pipeline_snapshot',
  salesCostRoster: 'sales_cost_roster',
  churnRetentionActuals: 'churn_retention_actuals',
  accountServiceAttachMap: 'account_service_attach_map',
} as const;

type CommercialEffectivenessEvidenceKey =
  (typeof COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS)[keyof typeof COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS];

// Mirrors SOURCE_CONFIGS in commercial-effectiveness.data-lake-source.ts.
export const COMMERCIAL_EFFECTIVENESS_CONTEXT_KIND_BY_EVIDENCE_KEY: Readonly<
  Record<CommercialEffectivenessEvidenceKey, string>
> = Object.freeze({
  approved_pipeline_snapshot: 'decision_agent:commercial_effectiveness:pipeline_snapshot',
  sales_cost_roster: 'decision_agent:commercial_effectiveness:sales_cost_roster',
  churn_retention_actuals: 'decision_agent:commercial_effectiveness:churn_retention',
  account_service_attach_map: 'decision_agent:commercial_effectiveness:account_attach',
});

export const COMMERCIAL_EFFECTIVENESS_SCHEMA_VERSIONS = {
  evidencePrepBundle: 'commercial_effectiveness_evidence_prep_bundle_v1',
} as const;

const NO_COMMERCIAL_EFFECTIVENESS_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors commercial-effectiveness.manifest.ts requiredState: all four are
// sourceClass modeled, domain commercial, missingBehavior block; only the
// pipeline snapshot is weekly.
const commercialEffectivenessEntry = (
  evidenceKey: CommercialEffectivenessEvidenceKey,
  description: string,
) =>
  defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
    contextKind: COMMERCIAL_EFFECTIVENESS_CONTEXT_KIND_BY_EVIDENCE_KEY[evidenceKey],
    schemaVersion: COMMERCIAL_EFFECTIVENESS_SCHEMA_VERSIONS.evidencePrepBundle,
    bindingPlaceholders: NO_COMMERCIAL_EFFECTIVENESS_BINDING_PLACEHOLDERS,
    domain: 'commercial',
    freshnessCadence:
      evidenceKey === COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS.pipelineSnapshot
        ? 'weekly'
        : 'monthly',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description,
  });

export const COMMERCIAL_EFFECTIVENESS_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'commercial-effectiveness',
  {
    pipeline_snapshot: commercialEffectivenessEntry(
      COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS.pipelineSnapshot,
      'Approved sales pipeline snapshot; backs win rate, pipeline coverage, funnel conversion, ' +
        'and cycle-time reads.',
    ),
    sales_cost_roster: commercialEffectivenessEntry(
      COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS.salesCostRoster,
      'Approved sales cost roster; the cost side of productivity, quota attainment, and ' +
        'comp-plan ROI, so commercial gains are never reported gross of what they cost.',
    ),
    churn_retention_actuals: commercialEffectivenessEntry(
      COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS.churnRetentionActuals,
      'Approved churn and retention actuals; backs net and gross revenue retention.',
    ),
    account_service_attach_map: commercialEffectivenessEntry(
      COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS.accountServiceAttachMap,
      'Approved account-to-service attach map; the whitespace basis for cross-sell reads.',
    ),
  },
  {
    evidenceKeys: Object.values(COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.modeled],
    contextKinds: Object.values(COMMERCIAL_EFFECTIVENESS_CONTEXT_KIND_BY_EVIDENCE_KEY),
    schemaVersions: Object.values(COMMERCIAL_EFFECTIVENESS_SCHEMA_VERSIONS),
    stateKeys: Object.values(COMMERCIAL_EFFECTIVENESS_EVIDENCE_KEYS),
    bindingPlaceholders: NO_COMMERCIAL_EFFECTIVENESS_BINDING_PLACEHOLDERS,
  },
);

export type CommercialEffectivenessModuleEvidenceContract =
  typeof COMMERCIAL_EFFECTIVENESS_MODULE_EVIDENCE_CONTRACT;
